import React, { createContext, useContext, useMemo } from "react";
import {
  ClerkProvider,
  useAuth,
  useClerk,
  useUser,
} from "@clerk/clerk-react";

const fallbackAuthState = {
  isLoaded: true,
  isSignedIn: false,
  userId: null,
  user: null,
  getToken: async () => null,
  signOut: async () => {},
};

const AuthContext = createContext(fallbackAuthState);

export const hasClerkConfig = Boolean(
  import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY
);

function ClerkBridge({ children }) {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();

  const value = useMemo(
    () => ({
      isLoaded,
      isSignedIn,
      userId,
      user,
      getToken,
      signOut,
    }),
    [isLoaded, isSignedIn, userId, user, getToken, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }) {
  if (!hasClerkConfig) {
    return <AuthContext.Provider value={fallbackAuthState}>{children}</AuthContext.Provider>;
  }

  return (
    <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}>
      <ClerkBridge>{children}</ClerkBridge>
    </ClerkProvider>
  );
}

export function useAuthState() {
  return useContext(AuthContext);
}
